import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit';
import { isBuyerForeign } from '@src/domains/customers';
import {
  AppDispatch,
  RootState,
  getClient,
  bearerAuth,
  validationErrorsWithoutI18n,
  getErrorMessage,
  changeLocation,
} from '@src/core/store';
import {
  Buyer,
  LoginRequest,
  SignupRequest,
  ResetPasswordRequest,
  RecoverPasswordRequest,
  ValidatePasswordRecoveryCodeRequest,
  VerifyPhoneRequest,
  UserRequest,
} from '@src/core/buyerApiClient';
import { AxiosError, AxiosResponse } from 'axios';
import * as storage from '@src/common/storage';
import { planneWidgetBuyerKey, readBuyerToken } from '@src/common/buyerToken';
import { track } from '@src/services/analytics';
import { dateWasReached } from '@src/common';
import { lockDateOn } from '@src/common/auth';
import { clearCartReference, setupCart } from '@src/features/cart/store/cart';
import { publicApiKeySelector, publicApiTokenSelector } from '@src/stores/app';

type PhoneVerificationStatus = 'inProgress' | 'error' | 'idle' | 'success';

interface AuthState {
  logged?: boolean;
  buyer?: Buyer;
  buyerToken?: string;
  errors: { [key: string]: { [key: string]: string[] } };
  flashMessage?: string;
  pendingSignup: boolean;
  createdAccount: boolean;
  resendEmail: boolean;
  fixEmail: boolean;
  codeResent: boolean;
  modifiedPassword: boolean;
  sendingForm: boolean;
  setupAuthOngoing: boolean;
  phoneVerification: {
    status: PhoneVerificationStatus;
    loading: boolean;
    requestLockedUntil?: string;
  };
  otp: {
    status: PhoneVerificationStatus;
    loading: boolean;
    requestLockedUntil?: string;
  };
  resetPassword: {
    loading: boolean;
    isCodeValid: boolean;
    code: string | undefined;
    email: string | undefined;
    requestLockedUntil?: string;
  };
}

const initialState: AuthState = {
  errors: {},
  createdAccount: false,
  pendingSignup: false,
  resendEmail: false,
  fixEmail: false,
  codeResent: false,
  modifiedPassword: false,
  sendingForm: false,
  setupAuthOngoing: true,
  phoneVerification: {
    status: 'idle',
    loading: false,
    requestLockedUntil: undefined,
  },
  otp: {
    status: 'idle',
    loading: false,
    requestLockedUntil: undefined,
  },
  resetPassword: {
    loading: false,
    isCodeValid: false,
    code: undefined,
    email: undefined,
    requestLockedUntil: undefined,
  },
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setBuyer: {
      reducer(state, action: PayloadAction<Buyer, string, { token: string; isNewAccount: boolean }>) {
        state.buyer = action.payload;
        state.buyerToken = action.meta.token;
        state.logged = true;
      },
      prepare(token: string, payload: Buyer, isNewAccount: boolean) {
        return { payload, meta: { token, isNewAccount } };
      },
    },
    clearBuyer: (state: AuthState) => {
      state.buyer = undefined;
      state.buyerToken = undefined;
      state.logged = false;
    },
    setErrors: {
      reducer(state, action: PayloadAction<{ [key: string]: string[] }, string, { key: string }>) {
        state.errors[action.meta.key] = action.payload;
      },
      prepare(key: string, payload: { [key: string]: string[] }) {
        return { payload, meta: { key } };
      },
    },
    setFlashMessage: (state: AuthState, action: PayloadAction<string, string>) => {
      state.flashMessage = action.payload;
    },
    ackFlashMessage: (state: AuthState) => {
      state.flashMessage = undefined;
    },
    setLogged: (state: AuthState, action: PayloadAction<boolean, string>) => {
      state.logged = action.payload;
    },
    setSetupAuthOngoing: (state: AuthState, action: PayloadAction<boolean, string>) => {
      state.setupAuthOngoing = action.payload;
    },
    requestSignup: (state: AuthState) => {
      state.pendingSignup = true;
    },
    receiveSignup: (state: AuthState) => {
      state.pendingSignup = false;
    },
    setCreatedAccount: (state: AuthState, action: PayloadAction<boolean>) => {
      state.createdAccount = action.payload;
    },
    setResendEmail: (state: AuthState, action: PayloadAction<boolean>) => {
      state.resendEmail = action.payload;
    },
    setFixEmail: (state: AuthState, action: PayloadAction<boolean>) => {
      state.fixEmail = action.payload;
    },
    setCodeResent: (state: AuthState, action: PayloadAction<boolean>) => {
      state.codeResent = action.payload;
    },
    setmodifiedPassword: (state: AuthState, action: PayloadAction<boolean>) => {
      state.modifiedPassword = action.payload;
    },
    setsendingForm: (state: AuthState, action: PayloadAction<boolean>) => {
      state.sendingForm = action.payload;
    },
    setPhoneVerificationLoading: (state: AuthState, action: PayloadAction<boolean>) => {
      state.phoneVerification.loading = action.payload;
    },
    setPhoneVerificationRequestLockedUntil: (state: AuthState, action: PayloadAction<number | undefined>) => {
      state.phoneVerification.requestLockedUntil = action.payload == undefined ? undefined : lockDateOn(action.payload);
    },
    setPhoneVerificationStatus: (state: AuthState, action: PayloadAction<PhoneVerificationStatus>) => {
      state.phoneVerification.status = action.payload;
    },
    setOtpLoading: (state: AuthState, action: PayloadAction<boolean>) => {
      state.otp.loading = action.payload;
    },
    setOtpRequestLockedUntil: (state: AuthState, action: PayloadAction<number | undefined>) => {
      state.otp.requestLockedUntil = action.payload == undefined ? undefined : lockDateOn(action.payload);
    },
    setOtpStatus: (state: AuthState, action: PayloadAction<PhoneVerificationStatus>) => {
      state.otp.status = action.payload;
    },
    setBuyerInfo: (state: AuthState, action: PayloadAction<Buyer>) => {
      state.buyer = action.payload;
    },
    setResetPasswordRequestLockedUntil: (state: AuthState, action: PayloadAction<number | undefined>) => {
      state.resetPassword.requestLockedUntil = action.payload == undefined ? undefined : lockDateOn(action.payload);
    },
    setResetPasswordLoading: (state: AuthState, action: PayloadAction<boolean>) => {
      state.resetPassword.loading = action.payload;
    },
    setResetPasswordCode: (state: AuthState, action: PayloadAction<string | undefined>) => {
      if (action.payload == undefined) {
        state.resetPassword.code = undefined;
        state.resetPassword.isCodeValid = false;
        return;
      }

      state.resetPassword.code = action.payload;
      state.resetPassword.isCodeValid = true;
    },
    setResetPasswordEmail: (state: AuthState, action: PayloadAction<string | undefined>) => {
      state.resetPassword.email = action.payload?.toLowerCase();
    },
    resetResetPassword: (state: AuthState) => {
      state.resetPassword = {
        loading: false,
        isCodeValid: false,
        code: undefined,
        email: undefined,
        requestLockedUntil: undefined,
      };
    },
  },
});

export default authSlice.reducer;

export const {
  setBuyer,
  clearBuyer,
  setErrors,
  setFlashMessage,
  ackFlashMessage,
  setLogged,
  requestSignup,
  receiveSignup,
  setCreatedAccount,
  setResendEmail,
  setFixEmail,
  setCodeResent,
  setmodifiedPassword,
  setsendingForm,
  setSetupAuthOngoing,
  setPhoneVerificationLoading,
  setPhoneVerificationRequestLockedUntil,
  setPhoneVerificationStatus,
  setBuyerInfo,
  setOtpLoading,
  setOtpRequestLockedUntil,
  setOtpStatus,
  setResetPasswordRequestLockedUntil,
  setResetPasswordLoading,
  setResetPasswordCode,
  setResetPasswordEmail,
  resetResetPassword,
} = authSlice.actions;

const getUserToken = (state: RootState) => readBuyerToken(publicApiKeySelector(state), state.auth.buyerToken);

export const setupAuth =
  (persist?: boolean, token?: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    const userToken = token ?? getUserToken(state);

    dispatch(setSetupAuthOngoing(true));
    if (!userToken) {
      dispatch(setLogged(false));
      dispatch(setSetupAuthOngoing(false));
      return;
    }

    try {
      const resp = await getClient(state).buyers.getOwnBuyer(bearerAuth(userToken!));
      const buyer = resp.data;

      dispatch(registerBuyer(userToken!, buyer, false, persist));
    } catch (e) {
      dispatch(logoutBuyer());
    } finally {
      dispatch(setSetupAuthOngoing(false));
    }
  };

export const refreshBuyerInfo = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();

  const userToken = getUserToken(state);
  if (!userToken) {
    return;
  }

  try {
    const { data } = await getClient(state).buyers.getOwnBuyer(bearerAuth(userToken!));

    dispatch(setBuyerInfo(data));
  } catch (e) {
    dispatch(logoutBuyer());
  }
};

export const registerBuyer =
  (token: string, buyer: Buyer, isNewAccount = false, persist = true) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const publicApiKey = publicApiTokenSelector(getState());

    dispatch(setBuyer(token, buyer, isNewAccount));

    if (persist) {
      storage.setValue(publicApiKey, planneWidgetBuyerKey, token);
    }
  };

export const logoutBuyer = (redirectLocation?: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const publicApiKey = publicApiTokenSelector(state);

  dispatch(clearBuyer());
  storage.removeValue(publicApiKey, planneWidgetBuyerKey);

  const {
    cart: { cart },
  } = state;
  if (cart?.customerId) {
    dispatch(clearCartReference());
    dispatch(setupCart(undefined, true));
  }

  if (redirectLocation) {
    dispatch(changeLocation(redirectLocation));
  }
};

export const login =
  (credentials: LoginRequest) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
    dispatch(setsendingForm(true));
    dispatch(setErrors('login', {}));

    let response: AxiosResponse<Buyer>;
    try {
      response = await getClient(getState()).buyers.buyerLogin(credentials);
    } catch (err) {
      const errors = validationErrorsWithoutI18n(err);
      if (errors) {
        dispatch(setErrors('login', errors));

        track('loginFail', {
          errors,
        });
        return false;
      } else if ((err as AxiosError).response?.status == 401) {
        dispatch(
          setErrors('login', {
            general: ['Credenciais inválidas'],
          }),
        );

        track('loginFail');
        return false;
      } else {
        throw err;
      }
    } finally {
      dispatch(setsendingForm(false));
    }

    const { data: buyer, headers } = response!;
    const token = headers['x-token']!;

    track('loginSuccess');

    dispatch(registerBuyer(token, buyer));

    return true;
  };

const EMAIL_CHANGE_ERROR_CONTEXT = 'emailChange';

const executeEmailChangeAction = async (
  dispatch: AppDispatch,
  getState: () => RootState,
  {
    action,
    successTrackEvent,
    failTrackEvent,
  }: {
    action: (state: RootState, userToken: string) => Promise<void>;
    successTrackEvent: string;
    failTrackEvent: string;
  },
): Promise<boolean> => {
  const state = getState();
  const userToken = getUserToken(state);

  if (!userToken) {
    return false;
  }

  dispatch(setsendingForm(true));
  dispatch(setErrors(EMAIL_CHANGE_ERROR_CONTEXT, {}));

  try {
    await action(state, userToken);
    track(successTrackEvent);

    return true;
  } catch (err) {
    const errors = validationErrorsWithoutI18n(err);

    track(failTrackEvent, { errors });

    if (errors) {
      dispatch(setErrors(EMAIL_CHANGE_ERROR_CONTEXT, errors));
      return false;
    }

    throw err;
  } finally {
    dispatch(setsendingForm(false));
  }
};

export const confirmEmailChange = (code: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
  return executeEmailChangeAction(dispatch, getState, {
    action: async (state, userToken) => {
      await getClient(state).buyers.confirmBuyerEmailChange({ code }, bearerAuth(userToken));
      await dispatch(refreshBuyerInfo());
    },
    successTrackEvent: 'emailChangeConfirmSuccess',
    failTrackEvent: 'emailChangeConfirmFail',
  });
};

export const declineEmailChange = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  return executeEmailChangeAction(dispatch, getState, {
    action: async (state, userToken) => {
      await getClient(state).buyers.declineBuyerEmailChange(bearerAuth(userToken));
    },
    successTrackEvent: 'emailChangeDeclineSuccess',
    failTrackEvent: 'emailChangeDeclineFail',
  });
};

export const signup = (credentials: SignupRequest) => async (dispatch: AppDispatch, getState: () => RootState) => {
  dispatch(requestSignup());
  dispatch(setErrors('signup', {}));
  dispatch(setsendingForm(true));

  let response: AxiosResponse<Buyer>;
  try {
    response = await getClient(getState()).buyers.buyerSignup(credentials);
  } catch (err) {
    const errors = validationErrorsWithoutI18n(err);

    track('signupFail', { errors });

    if (errors) {
      dispatch(setErrors('signup', errors));
      return false;
    } else {
      throw err;
    }
  } finally {
    dispatch(setsendingForm(false));
    dispatch(receiveSignup());
  }

  track('signupSuccess');

  const { data: buyer, headers } = response!;
  const token = headers['x-token']!;

  dispatch(registerBuyer(token, buyer, true));
  dispatch(setCreatedAccount(true));

  return true;
};

export const sendResetPasswordCode =
  (params: ResetPasswordRequest) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    if (!dateWasReached(resetPasswordSelector(state).requestLockedUntil)) {
      dispatch(setErrors('password_reset', { expiration: ['O código de verificação já foi enviado'] }));
      return;
    }

    dispatch(setResetPasswordLoading(true));
    dispatch(setErrors('password_reset', {}));

    try {
      await getClient(getState()).buyers.resetPassword(params);

      dispatch(setResetPasswordEmail(params.email));
    } catch (err) {
      if ((err as AxiosError).response?.status == 429) {
        dispatch(
          setErrors('phone_verification', {
            request: ['Não foi possível. Aguarde para solicitar um novo código'],
          }),
        );
        return;
      }

      const errors = validationErrorsWithoutI18n(err);
      if (errors) {
        dispatch(setErrors('password_reset', errors));
        return;
      } else {
        throw err;
      }
    } finally {
      dispatch(setResetPasswordLoading(false));

      dispatch(setResetPasswordRequestLockedUntil(1));
    }
  };

export const validateResetPasswordCode =
  (params: ValidatePasswordRecoveryCodeRequest, opts?: { trackEvents?: boolean }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setResetPasswordLoading(true));
    dispatch(setErrors('valid_code', {}));

    try {
      await getClient(getState()).buyers.validatePasswordRecoveryCode(params);

      dispatch(setResetPasswordCode(params.code));

      if (opts?.trackEvents) track('recoveryPasswordCodeStepSuccess');
    } catch (err) {
      if ((err as AxiosError).response?.status == 404) {
        dispatch(setErrors('valid_code', { code: ['Código Inválido'] }));
        return;
      }

      const errors = validationErrorsWithoutI18n(err);

      if (opts?.trackEvents) track('recoveryPasswordCodeStepError', { errors });

      if (errors) {
        dispatch(setErrors('valid_code', errors));

        return;
      } else {
        throw err;
      }
    } finally {
      dispatch(setResetPasswordLoading(false));
    }
  };

export const changePassword =
  (credentials: RecoverPasswordRequest, opts?: { trackEvents?: boolean; markModified?: boolean }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setResetPasswordLoading(true));
    dispatch(setErrors('password_recover', {}));

    let response: AxiosResponse<Buyer>;
    try {
      response = await getClient(getState()).buyers.recoverPassword(credentials);
    } catch (err) {
      const errors = validationErrorsWithoutI18n(err);

      if (opts?.trackEvents) track('recoveryPasswordRecoveryStepError', { errors });

      if (errors) {
        dispatch(setErrors('password_recover', errors));
        return false;
      } else {
        throw err;
      }
    } finally {
      dispatch(setsendingForm(false));
      dispatch(setResetPasswordLoading(false));
    }

    const { data: buyer, headers } = response!;
    const token = headers['x-token']!;

    if (opts?.trackEvents) track('recoveryPasswordRecoveryStepSuccess');
    if (opts?.markModified) dispatch(setmodifiedPassword(true));

    dispatch(registerBuyer(token, buyer));

    dispatch(setResetPasswordCode(undefined));
    dispatch(setResetPasswordEmail(undefined));

    dispatch(setResetPasswordLoading(false));

    return true;
  };

export const confirmEmailResend = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const email = passwordRecoveryEmailSelector(getState());
  if (email) {
    dispatch(sendResetPasswordCode({ email }));
    dispatch(setCodeResent(true));
    dispatch(setResendEmail(false));
  }
};

export const confirmEmailFix = () => async (dispatch: AppDispatch) => {
  dispatch(setResetPasswordEmail(''));
  dispatch(setResendEmail(false));
  dispatch(setFixEmail(true));
};

export const requestPhoneVerification = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();

  if (!dateWasReached(phoneVerificationSelector(state).requestLockedUntil)) {
    dispatch(setErrors('phone_verification', { expiration: ['O código de verificação já foi enviado'] }));
    return;
  }

  const userToken = getUserToken(state);
  if (!userToken) {
    return;
  }

  dispatch(setPhoneVerificationLoading(true));
  dispatch(setErrors('phone_verification', {}));

  try {
    await getClient(state).buyers.requestPhoneVerification(bearerAuth(userToken!));
  } catch (err) {
    if ((err as AxiosError).response?.status == 429) {
      dispatch(
        setErrors('phone_verification', {
          request: ['Não foi possível. Aguarde para solicitar um novo código'],
        }),
      );
      return;
    }

    const errors = validationErrorsWithoutI18n(err);
    if (errors) {
      dispatch(setErrors('phone_verification', errors));
      return;
    } else {
      throw err;
    }
  } finally {
    dispatch(setPhoneVerificationLoading(false));

    dispatch(setPhoneVerificationRequestLockedUntil(1));
  }
};

export const verifyPhone = (params: VerifyPhoneRequest) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const userToken = getUserToken(state);
  if (!userToken) {
    return;
  }

  dispatch(setPhoneVerificationLoading(true));
  dispatch(setPhoneVerificationStatus('inProgress'));
  dispatch(setErrors('phone_verification', {}));

  try {
    await getClient(state).buyers.verifyPhone(params, bearerAuth(userToken!));

    dispatch(setPhoneVerificationStatus('success'));

    dispatch(refreshBuyerInfo());
  } catch (err) {
    const errors = validationErrorsWithoutI18n(err);
    if (errors) {
      dispatch(setErrors('phone_verification', errors));
      dispatch(setPhoneVerificationStatus('error'));
      return;
    } else {
      throw err;
    }
  } finally {
    dispatch(setPhoneVerificationLoading(false));
  }
};

export const requestOtp =
  (params: ResetPasswordRequest) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
    const state = getState();

    if (!dateWasReached(otpSelector(state).requestLockedUntil)) {
      dispatch(setErrors('otp', { expiration: ['A chave de acesso já foi enviada'] }));
      return false;
    }

    dispatch(setOtpLoading(true));
    dispatch(setErrors('phone_verification', {}));

    try {
      await getClient(state).buyers.sendOtp(params);
    } catch (err) {
      if ((err as AxiosError).response?.status == 429) {
        dispatch(
          setErrors('otp', {
            request: ['Não foi possível. Aguarde para solicitar uma nova chave'],
          }),
        );
        return false;
      }

      const errors = validationErrorsWithoutI18n(err);
      if (errors) {
        dispatch(setErrors('otp', errors));
        return false;
      } else {
        throw err;
      }
    } finally {
      dispatch(setOtpLoading(false));

      dispatch(setOtpRequestLockedUntil(1));
    }

    return true;
  };

export const editUser = (params: UserRequest) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const userToken = getUserToken(state);
  if (!userToken) {
    return;
  }

  dispatch(setErrors('updateUser', {}));
  dispatch(setsendingForm(true));

  try {
    const { data } = await getClient(getState()).buyers.updateOwnBuyer(params, bearerAuth(userToken!));

    dispatch(setBuyerInfo(data));
  } catch (err) {
    const errors = validationErrorsWithoutI18n(err);

    track('updateUserFail', { errors });

    if (errors) {
      dispatch(setErrors('updateUser', errors));
      return;
    } else {
      throw err;
    }
  } finally {
    dispatch(setsendingForm(false));
    dispatch(receiveSignup());
  }

  track('updateUserSuccess');
};

export const buyerSelector = (state: RootState): [Buyer | undefined, boolean | undefined] => [
  state.auth.buyer,
  state.auth.logged,
];
const selectAuthErrors = (state: RootState) => state.auth.errors;

export const errorsSelector = createSelector([selectAuthErrors], (src) => {
  const dest: { [key: string]: { [key: string]: string[] } } = {};

  for (const key in src) {
    const value = src[key]!;
    const destValue: { [key: string]: string[] } = {};

    for (const key2 in value) {
      destValue[key2] = value[key2]!.map((errStr) => getErrorMessage(errStr));
    }

    dest[key] = destValue;
  }

  return dest;
});

const EMPTY_SIGNUP_ERRORS: Record<string, string[]> = {};

export const signupErrorsSelector = (state: RootState) => state.auth.errors['signup'] ?? EMPTY_SIGNUP_ERRORS;

export const hasEmailUniquenessErrorSelector = (state: RootState) =>
  signupErrorsSelector(state)['email']?.includes('emailMustBeUnique') ?? false;
export const flashMessageSelector = (state: RootState) => state.auth.flashMessage;
export const passwordRecoveryEmailSelector = (state: RootState) => state.auth.resetPassword.email;
export const passwordRecoveryCodeSelector = (state: RootState) => state.auth.resetPassword.code;
export const pendingSignupSelector = (state: RootState) => state.auth.pendingSignup;
export const createdAccountSelector = (state: RootState) => state.auth.createdAccount;
export const resendEmailSelector = (state: RootState) => state.auth.resendEmail;
export const fixEmailSelector = (state: RootState) => state.auth.fixEmail;
export const codeResentSelector = (state: RootState) => state.auth.codeResent;
export const modifiedPasswordSelector = (state: RootState) => state.auth.modifiedPassword;
export const sendingFormSelector = (state: RootState) => state.auth.sendingForm;
export const setupAuthOngoingSelector = (state: RootState) => state.auth.setupAuthOngoing;
export const phoneVerificationSelector = (state: RootState) => state.auth.phoneVerification;
export const otpSelector = (state: RootState) => state.auth.otp;
export const authSelector = (state: RootState) => state.auth;
export const resetPasswordSelector = (state: RootState) => state.auth.resetPassword;

// No buyer means not foreign; a logged buyer without a country code is foreign.
export const buyerIsForeign = ({ auth: { buyer } }: RootState) => buyer != null && isBuyerForeign(buyer.countryCode);
export const buyerNeedsPhoneVerificationSelector = () => false;
